/**
 * Report date helpers.
 *
 * Reports default to the LATEST BUSINESS DAY WITH DATA — the lake's
 * MAX(as_of_date), persisted from the /codenames payload at login
 * (STORAGE_KEYS.LATEST_DATA_DATE). That value is authoritative: it already
 * accounts for weekends, market holidays, AND data-delivery delays, so the date
 * inputs never default to (or cap at) a no-data day. Until it has loaded we fall
 * back to the most recent weekday (a thin offline guard that only skips Sat/Sun);
 * the lake value supersedes it as soon as the preload lands.
 *
 * The CEILING is a second, separate value (STORAGE_KEYS.MAX_SELECTABLE_DATE):
 * the same snapshot date walked forward over closed market days by the backend,
 * which owns the holiday calendar. It is what bounds the date inputs, so today's
 * own Saturday stays selectable even though no Saturday snapshot exists - the
 * server keeps that label, carries values forward from Friday and advances bond
 * accrued. Absent (older payload, failed lookup) it degrades to the snapshot date.
 */
import { STORAGE_KEYS } from '../../../config/constants';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** The persisted authoritative latest data date ('YYYY-MM-DD'), or null. */
export function getLatestDataDate() {
  try {
    const v = sessionStorage.getItem(STORAGE_KEYS.LATEST_DATA_DATE);
    return v && ISO_DATE_RE.test(v) ? v : null;
  } catch (_) {
    return null;
  }
}

const latestDataDateListeners = new Set();

/** Subscribe to latest-data-date writes (for useSyncExternalStore, so a mounted
 *  "Data as of" chip follows a refresh). Returns the unsubscribe. */
export function subscribeLatestDataDate(listener) {
  latestDataDateListeners.add(listener);
  return () => latestDataDateListeners.delete(listener);
}

/** Persist the authoritative latest data date (called from every /codenames load). */
export function setLatestDataDate(iso) {
  try {
    if (iso && ISO_DATE_RE.test(iso)) {
      sessionStorage.setItem(STORAGE_KEYS.LATEST_DATA_DATE, iso);
      latestDataDateListeners.forEach((listener) => listener());
    }
  } catch (_) {
    /* storage unavailable — getters fall back to last-weekday */
  }
}

/** The backend's picker ceiling ('YYYY-MM-DD'), or null. */
export function getMaxSelectableDate() {
  try {
    const v = sessionStorage.getItem(STORAGE_KEYS.MAX_SELECTABLE_DATE);
    return v && ISO_DATE_RE.test(v) ? v : null;
  } catch (_) {
    return null;
  }
}

/** Persist the backend's picker ceiling (called from the /codenames load).
 *  An absent or malformed value CLEARS the key rather than leaving the previous
 *  one in place: a payload with no ceiling (older backend, or a failed holiday
 *  lookup) must fall back to latest_data_date, not to a stale closed-market day. */
export function setMaxSelectableDate(iso) {
  try {
    if (iso && ISO_DATE_RE.test(iso)) {
      sessionStorage.setItem(STORAGE_KEYS.MAX_SELECTABLE_DATE, iso);
    } else {
      sessionStorage.removeItem(STORAGE_KEYS.MAX_SELECTABLE_DATE);
    }
  } catch (_) {
    /* storage unavailable - getters fall back to latest_data_date */
  }
}

/** Local 'YYYY-MM-DD' for a Date (avoids toISOString's UTC skew near midnight). */
export function toReportDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Reformat a 'YYYY-MM-DD' string to 'MM/DD/YYYY' for display. String-only (no
 *  Date object, so no timezone skew); a null/malformed input passes through. */
export function isoToMDY(iso) {
  const m = typeof iso === 'string' && iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : iso;
}

/** Parse a 'YYYY-MM-DD' string as a LOCAL date (not UTC midnight). */
function parseLocalDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Walk `d` back to the most recent weekday (Mon-Fri). Offline fallback only —
 *  it can't know market holidays or data delays; the lake date does. */
function toLastWeekday(d) {
  const x = new Date(d);
  while (x.getDay() === 0 || x.getDay() === 6) {
    x.setDate(x.getDate() - 1);
  }
  return x;
}

/** Date object for the latest selectable as-of date: the backend's ceiling when
 *  known (the lake's latest data date already walked forward over closed market
 *  days), else that latest data date, else the most recent weekday before today.
 *
 *  The ceiling can be LATER than any snapshot the lake holds - that is the point.
 *  A closed-market day is servable (values carry forward from the prior business
 *  day, bond accrued advances), and only the backend knows the holiday calendar,
 *  so the walk cannot be done here. */
export function maxReportDateObj() {
  const latest = getMaxSelectableDate() || getLatestDataDate();
  if (latest) return parseLocalDate(latest);
  const d = new Date();
  d.setDate(d.getDate() - 1); // Pershing data lands for the PRIOR day
  return toLastWeekday(d);
}

/** Latest selectable as-of date as 'YYYY-MM-DD' — use for the date input `max`. */
export function maxReportDate() {
  return toReportDate(maxReportDateObj());
}

/** Date object for the DEFAULT as-of / through date: the latest business day
 *  WITH DATA (the lake's latest snapshot) - deliberately NOT the extended
 *  ceiling from `maxReportDateObj`. The ceiling walks forward across closed
 *  market days so today's own weekend can be SELECTED; defaulting to it too
 *  would silently walk every Monday's report onto Sunday (extra accrued /
 *  carry-forward vs the Friday book, with no user action). Falls back to the
 *  most recent weekday before today when the lake date hasn't loaded yet
 *  (offline guard only). */
export function defaultReportDateObj() {
  const latest = getLatestDataDate();
  if (latest) return parseLocalDate(latest);
  const d = new Date();
  d.setDate(d.getDate() - 1); // Pershing data lands for the PRIOR day
  return toLastWeekday(d);
}

/** Default as-of / through date ('YYYY-MM-DD'): the latest business day with
 *  data. NOT the picker ceiling - see `defaultReportDateObj`; use
 *  `maxReportDate` for the input `max`. */
export function defaultReportDate() {
  return toReportDate(defaultReportDateObj());
}

/** `n` days before the default - for range-report "from" defaults. */
export function reportDateDaysAgo(n) {
  const d = defaultReportDateObj();
  d.setDate(d.getDate() - n);
  return toReportDate(d);
}

/** January 1 of the latest-data year — the "begin = Jan 1 of the current year"
 *  default for range reports. Uses the data year (not calendar) so begin/end stay
 *  in the same year even if data lags into early January. */
export function firstOfDataYear() {
  return toReportDate(new Date(defaultReportDateObj().getFullYear(), 0, 1));
}

/** December 31 of the year BEFORE the latest-data year - the default "from" of a
 *  year-to-date performance range. A performance window links (from, through],
 *  so its from date is the BASE close: from Dec 31 the window links Jan 1's own
 *  day, exactly as the Performance Summary's YTD does; from Jan 1 it skipped that
 *  day (pid 1774 booked -198.6 bp on 2026-01-01). */
export function lastOfPriorDataYear() {
  return toReportDate(new Date(defaultReportDateObj().getFullYear() - 1, 11, 31));
}

/** Cap a 'YYYY-MM-DD' at the latest selectable data date. Applied to EVERY date
 *  we send: the lake cannot serve a date past its newest snapshot. Weekends and
 *  market holidays are NOT moved - the server keeps the requested label, carries
 *  positions and prices forward from the prior business day, and advances bond
 *  accrued through the requested date. Returns the input unchanged if unparseable. */
export function capToMaxDate(iso) {
  if (!iso || !ISO_DATE_RE.test(iso)) return iso;
  const d = parseLocalDate(iso);
  const cap = maxReportDateObj();
  return toReportDate(d > cap ? cap : d);
}

/** `capToMaxDate` plus a walk-back to the prior weekday. Still used for a period
 *  START, where re-anchoring the opening value would silently change period
 *  returns, realized G/L windows and cash ledgers. */
export function snapToBusinessDay(iso) {
  if (!iso || !ISO_DATE_RE.test(iso)) return iso;
  let d = parseLocalDate(iso);
  const cap = maxReportDateObj();
  if (d > cap) d = new Date(cap);
  return toReportDate(toLastWeekday(d));
}
